/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { IAssetInfo } from "../../models/asset";
import { useFormik } from "formik";
import { AssetSummary } from "../../commons/assetSummary";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import AssetSearchSelector from "../AssetSearchSelector";
import View from "../Container";
import Text from "../Text";
import Label from "../Label";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import Button from "../Button";
import { InputText } from "primereact/inputtext";

interface IInsertCustomizedEventForm {
    selectedAsset?: AssetSummary;
    date?: Date;
    aportValue?: number;
    name?: string;
}

interface IInsertCustomizedEventHandleFormProps {
    onSuccess: () => void;
}

const InsertCustomizedEventHandleForm = ({ onSuccess }: IInsertCustomizedEventHandleFormProps) => {
    const http = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const chargeMutation = useMutation({
        mutationFn: async (form: IInsertCustomizedEventForm) => {
            const body = {
                event_date: moment(form.date).format("YYYY-MM-DD HH:mm:ss"),
                event_value: form.aportValue,
                event_name: form.name
            };

            return await http.doPatch<any, IAssetInfo>(`asset/${form.selectedAsset?.id}/event`, body);
        }
    });

    const formik = useFormik<IInsertCustomizedEventForm>({
        initialValues: {
            selectedAsset: undefined,
            date: undefined,
            aportValue: 0,
            name: ""
        },
        onSubmit: async (values) => {
            await send(values);
        },
        validationSchema: () => Yup
            .object()
            .shape({
                selectedAsset: Yup.object().required("Campo obrigatório"),
                date: Yup.date().required("Campo obrigatório"),
                aportValue: Yup.number()
                    .min(1, "Valor mínimo obrigatório")
                    .required("Campo obrigatório"),
                name: Yup.string().required("Campo obrigatório")
            })
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const {
        isPre,
        indexCorrelationName,
        indexCorrelation,
        issuingBankName,
        negotiatingBankName,
        dueDate,
        purchaseDate,
        brokerageValue,
        othersValue,
        monthlyCosts,
        quantity,
        yearTax,
        cotation,
        recurringValue,
        pbType
    } = formik.values.selectedAsset ?? {};

    const toCurrency = (value: number) => {
        return Intl.NumberFormat(
            "pt-BR",
            {
                style: "currency",
                currency: "BRL"
            }
        )
            .format(value);
    }

    const infos: { label: string; value: string; }[] = [];
    if (pbType) {
        infos.push({
            label: "Ativo",
            value: `${pbType} ${isPre ? "pré-fixado" : "pós-fixado"}`
        });
    }

    if (indexCorrelation) {
        infos.push({
            label: "Taxa",
            value: `${indexCorrelationName} + ${indexCorrelation}%`
        });
    }

    if (dueDate) {
        infos.push({
            label: "Vencimento",
            value: moment(dueDate).format("DD/MM/YYYY")
        });
    }

    if (purchaseDate) {
        infos.push({
            label: "Compra",
            value: moment(purchaseDate).format("DD/MM/YYYY")
        });
    }

    if (issuingBankName) {
        infos.push({
            label: "Emissor",
            value: issuingBankName
        });
    }

    if (negotiatingBankName) {
        infos.push({
            label: "Corretora",
            value: negotiatingBankName
        });
    }

    if (brokerageValue || othersValue) {
        infos.push({
            label: "Custos Operacionais",
            value: toCurrency((brokerageValue ?? 0) + (othersValue ?? 0))
        });
    }

    if (monthlyCosts) {
        infos.push({
            label: "Custos Mensais",
            value: toCurrency((monthlyCosts ?? 0))
        });
    }

    if (quantity) {
        infos.push({
            label: "Quantidade",
            value: quantity.toString()
        });
    }

    if (yearTax) {
        infos.push({
            label: "Taxa anual",
            value: `${yearTax}%`
        });
    }

    if (recurringValue) {
        infos.push({
            label: "Valor recorrente",
            value: toCurrency((recurringValue ?? 0))
        });
    }

    if (cotation) {
        infos.push({
            label: "Cotação",
            value: toCurrency((cotation ?? 0))
        });
    }

    const send = async (form: IInsertCustomizedEventForm) => {
        try {
            const res = await chargeMutation.mutateAsync(form);

            if ((res?.status ?? 0) > 299 || (res?.status ?? 0) <= 0) {
                throw new Error("Falha ao cadastrar investimento");
            }

            onSuccess();
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Falha ao cadastrar investimento"
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />

            <AssetSearchSelector
                label="Informe o ativo"
                hasError={!!formik.errors.selectedAsset && formik.touched.selectedAsset}
                error={formik.errors.selectedAsset}
                selected={formik.values.selectedAsset}
                onSelectBank={(asset: AssetSummary | null) => {
                    formik.setFieldValue("selectedAsset", asset);
                }}
            />

            <FormField
                id="date"
                label="Data do evento"
                hasError={!!formik.errors.date && formik.touched.date}
                error={formik.errors.date}
            >
                <Calendar
                    invalid={!!formik.errors.date && formik.touched.date}
                    value={formik.values.date}
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                    showIcon
                    disabledDays={[0, 6]}
                    onChange={(event) => {
                        formik.setFieldValue("date", event.value)
                    }}
                    locale="pt"
                />
            </FormField>

            <FormField
                id="aportValue"
                hasError={!!formik.errors.aportValue && formik.touched.aportValue}
                error={formik.errors.aportValue}
                label="Valor do evento"
            >
                <InputNumber
                    invalid={!!formik.errors.aportValue && formik.touched.aportValue}
                    value={formik.values.aportValue}
                    onValueChange={(e) =>
                        formik.setFieldValue("aportValue", e.value)
                    }
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                />
            </FormField>

            <FormField
                id="name"
                hasError={!!formik.errors.name && formik.touched.name}
                error={formik.errors.name}
                label="Apelido"
            >
                <InputText
                    invalid={!!formik.errors.name && formik.touched.name}
                    value={formik.values.name}
                    onChange={(e) =>
                        formik.setFieldValue("name", e.target.value)
                    }
                />
            </FormField>


            {
                !!infos.length && <View flexColumn gap={2}>
                    <Label className="border-bottom-1 border-gray-300 pb-2">
                        Dados do Investimento
                    </Label>

                    <View className="flex flex-column items-between gap-3">
                        {
                            infos.map(info => {
                                return (
                                    <View key={info.label} className="flex flex-column flex-row justify-between">
                                        <Label>{info.label}</Label>

                                        <Text className="text-xs max-w-xs">{info.value}</Text>
                                    </View>
                                )
                            })
                        }
                    </View>
                </View>
            }

            <View fullWidth>
                <Button
                    onClick={() => {
                        formik.handleSubmit();
                        return Promise.resolve();
                    }}
                    className="w-full justify-content-center"
                    loading={chargeMutation.isPending}
                    disabled={chargeMutation.isPending}
                >
                    Cadastrar
                </Button>
            </View>
        </>
    )
}

export default InsertCustomizedEventHandleForm;
